import { cloneDeep } from 'lodash';

import { ensureArray } from '@bv/utils';

interface DeepMergeOptions {
  mergeArrays?: boolean;
  clone?: boolean;
}

/**
 * Deeply merges a target object with one or more source objects.
 * Supports recursive merging of nested objects and optional merging of arrays.
 *
 * @template T - Type of the target object.
 * @template U - Type of the source object(s).
 *
 * @param {T} target - The object to merge into.
 * @param {U | [U, ...U[]]} source - A single source object or an array of source objects.
 * @param {DeepMergeOptions} [options={}] - Configuration options.
 * @param {boolean} [options.mergeArrays=false] - If true, arrays from both objects will be concatenated.
 *                                                If false, the source array will replace the target's.
 * @param {boolean} [options.clone=true] - If true, the function returns a new object without mutating the original target.
 *                                         If false, the target object will be mutated directly.
 *
 * @returns {T & U} A deeply merged object.
 */
export function deepMergeObjects<T extends Record<string, any>, U extends Record<string, any> = Record<string, any>>(
  target: T,
  source: U | [U, ...U[]],
  options: DeepMergeOptions = { mergeArrays: true, clone: true },
): T & U {
  const result: any = options.clone ? cloneDeep(target) : target;

  const sources = ensureArray(source);

  for (const currentSource of sources) {
    if (!currentSource || typeof currentSource !== 'object') {
      continue;
    }

    for (const key in currentSource) {
      if (Object.prototype.hasOwnProperty.call(currentSource, key)) {
        const sourceValue = currentSource[key];
        const targetValue = result[key];

        if (options.mergeArrays && Array.isArray(sourceValue) && Array.isArray(targetValue)) {
          result[key] = [...targetValue, ...sourceValue];
        } else if (
          sourceValue &&
          typeof sourceValue === 'object' &&
          !Array.isArray(sourceValue) &&
          targetValue &&
          typeof targetValue === 'object' &&
          !Array.isArray(targetValue)
        ) {
          result[key] = deepMergeObjects(targetValue, sourceValue, options);
        } else {
          result[key] = sourceValue;
        }
      }
    }
  }

  return result as T & U;
}

export default deepMergeObjects;
